import { templateToTitle } from '@apps/card/utils';
import { ThemeJson } from '@apps/card/routes/CardCustomizer';
import { Page } from '@apps/card/routes/CardCustomizer/components/Page';
import { PrimaryEventLocation } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/usePrimaryEventLocation';
import { fillPageWithEventInfo } from '@apps/card/utils/fillPageWithEventInfo';
import { StationeryTemplateCategoryEnum, useServiceCenterStationeryTemplatesContainerQuery } from '@graphql/generated';
import { EventInfoData } from '@shared/utils/eventInfo';
import useDebounce from '@shared/utils/hooks/useDebounce';
import { Flex, TextV2 } from '@withjoy/joykit';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { CATEGORIES, LOCATIONS, MOCK_EVENT_INFO, MOCK_LOCATION, MONTHS } from './PrintTemplates.constants';
import { Container, Header, Input, Label, Select, Sliders, StickyHeader, TextArea } from './PrintTemplates.styles';
import { getTimezoneOffset } from 'date-fns-tz';
import { useTranslation } from '@shared/core';

function camelCaseToWords(word: string) {
  const result = word.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

type EventInfoDataPlus = EventInfoData & {
  ownerMiddleName?: string;
  fianceeMiddleName?: string;
};

export const FILTER_PARAM_KEY = 'filter';
export const FILTER_DEFAULT = [''].join('\n');

function CheckboxGroup(props: { label: string; children: React.ReactNode }) {
  const { label, children } = props;
  return (
    <Flex
      flexDirection="column"
      gap={2}
    >
      <TextV2
        typographyVariant="label2"
        color="mono7"
        paddingBottom={2}
      >
        {label}
      </TextV2>
      {children}
    </Flex>
  );
}

function Checkbox<T>(props: { label: string; value: T; list: T[]; setList: (list: T[]) => void }) {
  const { label, value, list, setList } = props;
  return (
    <Label>
      <input
        type="checkbox"
        checked={list.includes(value)}
        onChange={e => setList(e.target.checked ? [...list, value] : list.filter(t => t !== value))}
      />
      <TextV2 typographyVariant="label2">{label}</TextV2>
    </Label>
  );
}

function parseName(name: string) {
  const [first, middle, ...lastParts] = name.split(/\s+/g);
  return !lastParts.length ? [first, '', middle ?? ''] : [first, middle, lastParts.join(' ')];
}

function NameInput(props: {
  fields: [keyof EventInfoDataPlus, keyof EventInfoDataPlus, keyof EventInfoDataPlus];
  eventInfo: EventInfoDataPlus;
  setEventInfo: (eventInfo: EventInfoDataPlus) => void;
}) {
  const { fields, eventInfo, setEventInfo } = props;
  const [firstField, middleField, lastField] = fields;
  const [value, setValue] = useState([eventInfo[firstField], eventInfo[middleField], eventInfo[lastField]].filter(Boolean).join(' '));

  return (
    <Input
      type="text"
      value={value}
      onChange={e => {
        const [first, middle, last] = parseName(e.target.value);
        setEventInfo({ ...eventInfo, [firstField]: first, [middleField]: middle, [lastField]: last });
        setValue(e.target.value);
      }}
    />
  );
}

export const PrintTemplates = () => {
  const { t } = useTranslation('stationery');
  const tNumerals = t('numerals');

  const urlParams = new URLSearchParams(window.location.search);
  const [filter, setFilter] = useState(urlParams.get(FILTER_PARAM_KEY) ?? FILTER_DEFAULT);
  const [startInput, setStartInput] = useState(0);
  const start = useDebounce(startInput, 500);
  const [tiers, setTiers] = useState(['premium']);
  const [categories, setCategories] = useState([StationeryTemplateCategoryEnum.invitation]);
  const [sides, setSides] = useState(['front']);
  const [display, setDisplay] = useState(['autofill']);
  const [eventInfo, setEventInfo] = useState<EventInfoDataPlus>(MOCK_EVENT_INFO);

  const [placeId, setPlaceId] = useState(LOCATIONS[0].placeId);
  const [monthIndex, setMonthIndex] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);
  const [location, setLocation] = useState<PrimaryEventLocation>(MOCK_LOCATION);

  useEffect(() => {
    const { Venue, Address, CityState, CityST, timezone } = LOCATIONS.find(location => location.placeId === placeId)!;
    const MINUTE_MS = 60 * 1000;
    const HOUR_MS = 60 * MINUTE_MS;
    // prettier-ignore
    const milliseconds = MONTHS[monthIndex]
      + (dayOfMonth - 1) * 24 * HOUR_MS
      + hour * HOUR_MS
      + minute * MINUTE_MS;
    const offsetMs = getTimezoneOffset(timezone, milliseconds);
    const utcMilliseconds = milliseconds - offsetMs;
    setLocation(location => ({ ...location, Venue, Address, CityState, CityST, utcMilliseconds, timezone }));
  }, [monthIndex, dayOfMonth, hour, minute, placeId]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const filterValue = filter.trim();
    if (filterValue == FILTER_DEFAULT) {
      url.searchParams.delete(FILTER_PARAM_KEY);
    } else {
      url.searchParams.set(FILTER_PARAM_KEY, filterValue);
    }
    history.pushState(null, '', url);
  }, [filter]);

  const { data, loading } = useServiceCenterStationeryTemplatesContainerQuery({
    variables: {
      filter: {},
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
  });

  if (loading) return <Container>Loading...</Container>;

  const templates = data?.stationeryTemplatesContainer.pagedResults.edges?.map(edge => edge.node) ?? [];
  const filterTerms = filter
    .split(/\n+/)
    .filter(term => term.length > 3)
    .map(term => term.toLowerCase().replaceAll(' ', '_')); // Make it easy to copy the paste the template title (instead of theme ID)
  const filteredTemplates = templates.filter(template => {
    if (template.themeId === 'test03') return false; // Invalid themeJSON payload
    if (template.themeId === 'INVALID') return false; // Invalid themeJSON payload
    if (template.id === '0dd0f9ff-3ad6-4945-a933-e19b741ed06b') return false; // Prototype Monogram

    const themeJSON = template.themeJSON as ThemeJson;
    const tier = themeJSON.card.front.layers.some(layer => layer.name === 'NAME1') ? 'basic' : 'premium';
    if (tiers.length && !tiers.includes(tier ?? '')) return false;
    if (categories.length && !categories.includes(template.category)) return false;
    if (sides.length && !sides.includes(template.side ?? '')) return false;

    if (!filterTerms.length) return true;
    return filterTerms.some(value => template.themeId.includes(value));
  });
  filteredTemplates.sort((a, b) => a.themeId.localeCompare(b.themeId));

  const end = Math.min(start + 50, filteredTemplates.length);

  return (
    <>
      <StickyHeader style={{ display: display.includes('autofill') ? undefined : 'none' }}>
        <NameInput
          fields={['ownerFirstName', 'ownerMiddleName', 'ownerLastName']}
          eventInfo={eventInfo}
          setEventInfo={setEventInfo}
        />
        <NameInput
          fields={['fianceeFirstName', 'fianceeMiddleName', 'fianceeLastName']}
          eventInfo={eventInfo}
          setEventInfo={setEventInfo}
        />
        <Select
          value={placeId}
          onChange={e => setPlaceId(e.target.value)}
        >
          {LOCATIONS.map(location => (
            <option
              key={location.placeId}
              value={location.placeId}
            >
              {location.Venue}
            </option>
          ))}
        </Select>
        <Sliders style={{ gridArea: '1 / 2 / 4' }}>
          <span>{new Date(MONTHS[monthIndex]).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}</span>
          <input
            type="range"
            min={0}
            max={MONTHS.length - 1}
            value={monthIndex}
            onChange={e => setMonthIndex(Number(e.target.value))}
            list="monthMarkers"
          />
          <span>{tNumerals.numericOrdinals[dayOfMonth]?.()}</span>
          <input
            type="range"
            min={1}
            max={31}
            value={dayOfMonth}
            onChange={e => setDayOfMonth(Number(e.target.value))}
          />
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {hour}:<span style={{ opacity: 0.3 }}>––</span>
          </span>
          <input
            type="range"
            min={0}
            max={23}
            value={hour}
            onChange={e => setHour(Number(e.target.value))}
          />
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ opacity: 0.3 }}>–</span>:{minute.toString().padStart(2, '0')}
          </span>
          <input
            type="range"
            min={0}
            max={59}
            value={minute}
            onChange={e => setMinute(Number(e.target.value))}
          />
        </Sliders>
      </StickyHeader>
      <Container>
        <Header>
          <TextArea
            value={filter}
            onChange={e => {
              setFilter(e.target.value);
              setStartInput(0);
            }}
            spellCheck={false}
            placeholder="Bohemian Eucalyptus&#10;Doodle Accommodations&#10;etc..."
          />
          <Flex
            alignItems="flex-start"
            gap={8}
          >
            <CheckboxGroup label="Tier">
              <Checkbox
                label="Premium"
                value="premium"
                list={tiers}
                setList={setTiers}
              />
              <Checkbox
                label="Basic"
                value="basic"
                list={tiers}
                setList={setTiers}
              />
            </CheckboxGroup>
            <CheckboxGroup label="Categories">
              <Flex gap={5}>
                <Flex
                  flexDirection="column"
                  gap={2}
                >
                  {CATEGORIES.slice(0, 3).map(category => (
                    <Checkbox
                      key={category.key}
                      label={category.label}
                      value={category.key}
                      list={categories}
                      setList={setCategories}
                    />
                  ))}
                </Flex>
                <Flex
                  flexDirection="column"
                  gap={2}
                >
                  {CATEGORIES.slice(3, 6).map(category => (
                    <Checkbox
                      key={category.key}
                      label={category.label}
                      value={category.key}
                      list={categories}
                      setList={setCategories}
                    />
                  ))}
                </Flex>
                <Flex
                  flexDirection="column"
                  gap={2}
                >
                  {CATEGORIES.slice(6).map(category => (
                    <Checkbox
                      key={category.key}
                      label={category.label}
                      value={category.key}
                      list={categories}
                      setList={setCategories}
                    />
                  ))}
                </Flex>
              </Flex>
            </CheckboxGroup>
            <CheckboxGroup label="Side">
              <Checkbox
                label="Front"
                value="front"
                list={sides}
                setList={setSides}
              />
              <Checkbox
                label="Back"
                value="back"
                list={sides}
                setList={setSides}
              />
            </CheckboxGroup>
            <CheckboxGroup label="Display">
              <Checkbox
                label="Auto-fill"
                value="autofill"
                list={display}
                setList={setDisplay}
              />
              <Checkbox
                label="Labels"
                value="labels"
                list={display}
                setList={setDisplay}
              />
            </CheckboxGroup>
            <Flex flex="1 1 auto" />
            <Flex
              alignItems="center"
              gap={3}
            >
              <Input
                type="number"
                value={startInput}
                onChange={e => setStartInput(parseInt(e.target.value))}
                min={0}
                max={filteredTemplates.length}
              />
              to {end} of {filteredTemplates.length} Results
            </Flex>
          </Flex>
        </Header>
        <Flex
          flexDirection="row"
          flexWrap="wrap"
          gap={7}
        >
          {filteredTemplates.slice(start, end).map(template => {
            const { themeId, category, side, widthInches, heightInches } = template;
            const page = cloneDeep(template.themeJSON.card.front);
            if (display.includes('autofill')) fillPageWithEventInfo(page, eventInfo, location, tNumerals);

            return (
              <Flex
                key={template.id}
                flexDirection="column"
                gap={4}
                style={{ scale: 2 }}
              >
                {display.includes('labels') && (
                  <Flex flexDirection="column">
                    <TextV2
                      typographyVariant="label2"
                      title={themeId}
                    >
                      {templateToTitle(template)}
                    </TextV2>
                    <TextV2
                      typographyVariant="label2"
                      title={themeId}
                    >
                      {[camelCaseToWords(category), `${widthInches}×${heightInches}`, side === 'back' ? 'Back' : ''].filter(x => x).join(' ')}
                    </TextV2>
                    <TextV2
                      typographyVariant="label2"
                      color="mono7"
                    >
                      {template.id}
                    </TextV2>
                  </Flex>
                )}
                <Page
                  page={page}
                  width={5}
                  height={7}
                  fill={template.category === StationeryTemplateCategoryEnum.monogram ? 'white' : undefined}
                />
              </Flex>
            );
          })}
        </Flex>
      </Container>
    </>
  );
};
